<template>
  <div class="table-shadow bg-dark-green-title table-editable">
    <table class="table-striped table bg-table-cell-dark no-wrap">
      <thead>
        <tr>
          <th v-if="hasActions"></th>
          <th>Vendor</th>
          <th>Area</th>
          <th>Service level</th>
          <th>Amount</th>
          <th>Country Group</th>
          <th>Price per piece</th>
          <th>Price per kg</th>
          <th>Minimum price</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="price in basePrices" :key="price.id">
          <td v-if="hasActions">
            <div v-if="!price.isDefault && (priceType === BASE || priceType === price.type)" class="d-flex">
              <span class="btn-table-action" @click="openModal(EDIT_PRICE_MODAL, price)">
                <mdicon name="pencil-outline" class="pointer" size="18" />
              </span>
              <span class="btn-table-action pl-2" @click="deletePrice(price)">
                <mdicon name="TrashCanOutline" class="pointer" size="18" />
              </span>
            </div>
          </td>
          <td>{{ carrierHandler2Name(price.carrierHandle) }}</td>
          <td>{{ ucFirst(price.destination) }}</td>
          <td>{{ ucFirst(price.serviceLevel) }}</td>
          <td>>{{ price.minPackagesAmount }}</td>
          <td>{{ price.countryGroup ? formatCountry(price.countryGroup) : price.countryCode }}</td>
          <td v-if="!isWeightMode(price)" class="p-0">
            <div class="form-group form-group_bg_white">
              <input
                v-model.number="price.pricePerPiece"
                type="number"
                class="form-control form-control_hide_arrow"
                step="0.01"
                @input="updatePrice(price, 'pricePerPiece')"
              />
              <div
                v-if="getBasePrice(price) && getBasePrice(price).pricePerPiece && isHasDefaultPrices"
                class="base-price"
              >
                {{ getBasePrice(price).pricePerPiece }}
              </div>
            </div>
          </td>
          <td v-if="!isWeightMode(price)" colspan="1" class="p-0">
            <div class="form-group form-group_bg_white">
              <input
                v-model.number="price.pricePerKg"
                type="number"
                class="form-control form-control_hide_arrow"
                step="0.01"
                @input="updatePrice(price, 'pricePerKg')"
              />
              <div
                v-if="getBasePrice(price) && getBasePrice(price).pricePerKg && isHasDefaultPrices"
                class="base-price"
              >
                {{ getBasePrice(price).pricePerKg }}
              </div>
            </div>
          </td>
          <td v-if="!isWeightMode(price)" colspan="1" class="p-0">
            <div class="form-group form-group_bg_white">
              <input
                v-model.number="price.minPrice"
                type="number"
                class="form-control form-control_hide_arrow"
                step="0.01"
                @input="updatePrice(price, 'minPrice')"
              />
              <div v-if="getBasePrice(price) && getBasePrice(price).minPrice && isHasDefaultPrices" class="base-price">
                {{ getBasePrice(price).minPrice }}
              </div>
            </div>
          </td>
          <td v-if="isWeightMode(price)" class="p-0" colspan="3">
            <button
              class="btn btn-success btn-lg btn-bright-green"
              @click="openModal(EDIT_PRICE_WEIGHT, { price, isHasDefaultPrices })"
            >
              Apply custom logic
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <EditBasePriceWeight :id="EDIT_PRICE_WEIGHT" />
  <EditPriceModal :id="EDIT_PRICE_MODAL" />
</template>

<script lang="ts">
import type { BasePrice } from '@/types/Models/BasePrice'
import { computed, defineComponent, onMounted, type ComputedRef, type PropType } from 'vue'
import { EDIT_PRICE_MODAL, EDIT_PRICE_WEIGHT } from '@/const/ModalTypes'
import { BASE } from '@/const/PriceTypes'
import { carrierHandler2Name, ucFirst } from '@/services/Helpers'
import { openModal, registerModals } from '@/services/Modal'
import { usePricesStore } from '@/store/prices'
import EditPriceModal from '@/views/Modals/BasePrice/EditPriceModal.vue'
import EditBasePriceWeight from '@/views/Modals/BasePrice/EditBasePriceWeight.vue'

export default defineComponent({
  name: 'BasePriceTable',
  components: { EditPriceModal, EditBasePriceWeight },
  props: {
    prices: {
      type: Object as PropType<BasePrice[]>,
      required: true,
    },
    defaultPrices: {
      type: Object as PropType<BasePrice[]>,
      default: () => [] as BasePrice[],
    },
  },
  emits: ['update-price', 'remove-price'],
  setup(props, { emit }) {
    const pricesStore = usePricesStore()

    const basePrices: ComputedRef<BasePrice[]> = computed(() => JSON.parse(JSON.stringify(props.prices)))
    const isHasDefaultPrices: ComputedRef<boolean> = computed(() => props.defaultPrices.length > 0)
    const priceType: ComputedRef<string> = computed(() => pricesStore.type)
    const hasActions: ComputedRef<boolean> = computed(() =>
      basePrices.value.some(
        (price) => !price.isDefault && (priceType.value === BASE || priceType.value === price.type),
      ),
    )

    const updatePrice = (price: BasePrice, key: keyof BasePrice) => {
      const basePrice: BasePrice = { ...price }
      if (key === 'pricePerPiece' || key === 'pricePerKg' || key === 'minPrice') {
        basePrice[key] = Number(price[key])
        emit('update-price', basePrice)
      }
    }
    const deletePrice = (price: BasePrice) => {
      emit('remove-price', price)
    }
    const isWeightMode = (price: BasePrice) => price.mode === 'weight'
    const formatCountry = (country: string | null) =>
      !country ? '' : country === 'eu' ? 'European Union' : 'Other Countries'
    const getBasePrice: any | BasePrice = (price: BasePrice | null) => {
      const basePrice = props.defaultPrices.find(
        (el) =>
          el?.minPackagesAmount === price?.minPackagesAmount &&
          el?.countryGroup === price?.countryGroup &&
          el?.carrierHandle === price?.carrierHandle &&
          el?.destination === price?.destination &&
          el?.serviceLevel === price?.serviceLevel,
      )
      return basePrice || {}
    }

    onMounted(() => {
      registerModals(EDIT_PRICE_WEIGHT)
      registerModals(EDIT_PRICE_MODAL)
    })

    return {
      priceType,
      basePrices,
      hasActions,
      isHasDefaultPrices,
      openModal,
      updatePrice,
      isWeightMode,
      formatCountry,
      getBasePrice,
      carrierHandler2Name,
      ucFirst,
      deletePrice,
      EDIT_PRICE_WEIGHT,
      EDIT_PRICE_MODAL,
      BASE,
    }
  },
})
</script>
